/**
 * Per-browser pairing id used to bind an unlock code to THIS browser.
 *
 * The web shows this id; the user types it into the phone app, which bakes it
 * into the unlock code's `sub` claim. The web then only accepts a code whose
 * `sub` matches this browser's id — so a shared code won't verify anywhere else.
 * No server, no accounts: the id lives only in this browser's localStorage.
 */
const KEY = "carspend_pair_id";
// Crockford-ish alphabet: no I/O/0/1 to keep the code easy to read and type.
const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

/** Get (or lazily create) this browser's pairing id — 10 chars, ~50 bits. */
export function getPairId(): string {
  let v = localStorage.getItem(KEY);
  if (!v) {
    const bytes = new Uint8Array(10);
    crypto.getRandomValues(bytes);
    v = Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join("");
    localStorage.setItem(KEY, v);
  }
  return v;
}

/** Canonical form for comparison: letters/digits only, uppercased. */
export function normalizePairId(s: string): string {
  return s.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

/** Grouped for display, e.g. "ABCDE-FGHJK". */
export function formatPairId(s: string): string {
  const n = normalizePairId(s);
  return n.match(/.{1,5}/g)?.join("-") ?? n;
}
